import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { Switch } from "@/components/ui/switch";

export default function AdminDemoWidget() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: true,
    greeting_message: '',
    balloon_text: '',
    balloon_delay: 3000,
    position: 'right',
    primary_color: '#000000'
  });
  
  const [salonInfo, setSalonInfo] = useState('');

  useEffect(() => {
    fetchSettings();
    fetchSalonInfo();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/demo-widget/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching demo widget settings:', error);
      toast.error('Ошибка загрузки настроек');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSalonInfo = async () => {
    try {
      const response = await axios.get(`${API}/admin/demo-widget/info`);
      setSalonInfo(response.data.salon_info || '');
    } catch (error) {
      console.error('Error fetching salon info:', error);
      toast.error('Ошибка загрузки информации');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/admin/demo-widget/settings`, settings);
      toast.success('✅ Настройки сохранены!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Ошибка сохранения настроек');
    } finally {
      setSaving(false);
    }
  };
  
  const handleSaveSalonInfo = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/admin/demo-widget/info`, { salon_info: salonInfo });
      toast.success('✅ Информация сохранена!');
    } catch (error) {
      console.error('Error saving business info:', error);
      toast.error('Ошибка сохранения информации');
    } finally {
      setSaving(false);
    }
  };
  
 

  if (loading) {
    return (
      <div className="container mx-auto px-3 md:px-4 py-8">
        <div className="text-center">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
      <Card className="border-2 border-black">
        <CardHeader>
          <CardTitle className="text-xl md:text-2xl">🖥️ Demo Виджет на главной странице</CardTitle>
          <CardDescription>
            Настройте демо-виджет для привлечения новых клиентов
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 border-2 border-black rounded-lg bg-blue-50">
            <div>
              <Label className="text-lg font-bold">Включить Demo Виджет</Label>
              <p className="text-sm text-gray-600 mt-1">
                Показывать виджет на главной странице сайта
              </p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
              className="data-[state=checked]:bg-black"
            />
          </div>

          {/* Greeting Message */}
          <div>
            <Label htmlFor="greeting" className="text-base font-bold">
              Приветственное сообщение
            </Label>
            <p className="text-sm text-gray-600 mb-2">
              Первое сообщение от бота при открытии виджета
            </p>
            <textarea
              id="greeting"
              value={settings.greeting_message}
              onChange={(e) => setSettings({ ...settings, greeting_message: e.target.value })}
              className="w-full p-3 border-2 border-black rounded-md"
              rows="3"
              placeholder="Хотите такой же виджет для своего сайта? Зарегистрируйтесь!"
            />
          </div>

          {/* Balloon Settings */}
          <div className="p-4 border-2 border-purple-300 rounded-lg bg-purple-50">
            <h3 className="font-bold text-lg mb-3">🎈 Всплывающая подсказка (Balloon)</h3>
            
            <div className="space-y-3">
              <div>
                <Label htmlFor="balloon-text">Текст подсказки</Label>
                <Input
                  id="balloon-text"
                  value={settings.balloon_text}
                  onChange={(e) => setSettings({ ...settings, balloon_text: e.target.value })}
                  className="border-2 border-black"
                  placeholder="💬 Хочешь такой же виджет? Регистрируйся!"
                />
              </div>
              
              <div>
                <Label htmlFor="balloon-delay">Задержка показа (мс)</Label>
                <Input
                  id="balloon-delay"
                  type="number"
                  value={settings.balloon_delay}
                  onChange={(e) => setSettings({ ...settings, balloon_delay: parseInt(e.target.value) })}
                  className="border-2 border-black"
                  min="1000"
                  step="1000"
                />
                <p className="text-xs text-gray-600 mt-1">
                  Через сколько миллисекунд показать подсказку (3000 = 3 секунды)
                </p>
              </div>
            </div>
          </div>

          {/* Business Info - Как у клиентов */}
          <div className="p-4 border-2 border-green-300 rounded-lg bg-green-50">
            <div className="mb-4">
              <h3 className="font-bold text-lg">🏢 Информация для AI (как у клиентов)</h3>
              <p className="text-sm text-gray-600 mt-1">
                AI будет отвечать на основе этой информации. Работает точно так же как у клиентов!
              </p>
            </div>

            <div>
              <Label htmlFor="business-info" className="font-semibold">
                Информация о вашем бизнесе
              </Label>
              <textarea
                id="business-info"
                value={salonInfo}
                onChange={(e) => setSalonInfo(e.target.value)}
                className="w-full min-h-[400px] p-3 border-2 border-black rounded-md mt-2 font-mono text-sm"
                placeholder="Опишите ваш бизнес: услуги, цены, тарифы, функции, преимущества, контакты и т.д..."
              />
              <div className="flex items-center justify-between mt-2">
                <p className="text-xs text-gray-600">
                  💡 AI будет использовать эту информацию для ответов на вопросы посетителей
                </p>
                <p className="text-xs text-gray-500">
                  {salonInfo?.length || 0} / 10,000 символов
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-4 pt-4 border-t-2 border-green-600">
              <Button
                onClick={handleSaveSalonInfo}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white font-bold w-full"
              >
                {saving ? '💾 Сохранение...' : '💾 Сохранить информацию'}
              </Button>
            </div>
          </div>

          {/* Visual Settings */}
          <div className="p-4 border-2 border-blue-300 rounded-lg bg-blue-50">
            <h3 className="font-bold text-lg mb-3">🎨 Внешний вид</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="position">Позиция виджета</Label>
                <select
                  id="position"
                  value={settings.position}
                  onChange={(e) => setSettings({ ...settings, position: e.target.value })}
                  className="w-full p-2 border-2 border-black rounded-md mt-1"
                >
                  <option value="right">Справа внизу</option>
                  <option value="left">Слева внизу</option>
                </select>
              </div>

              <div>
                <Label htmlFor="color">Основной цвет</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="color"
                    type="color"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    className="w-20 h-10 border-2 border-black"
                  />
                  <Input
                    type="text"
                    value={settings.primary_color}
                    onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                    className="flex-1 border-2 border-black"
                    placeholder="#000000"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex gap-3 pt-4 border-t-2 border-black">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-black text-white hover:bg-gray-800 font-bold flex-1"
            >
              {saving ? '💾 Сохранение...' : '💾 Сохранить настройки'}
            </Button>
            <Button
              onClick={fetchSettings}
              variant="outline"
              className="border-2 border-black"
            >
              🔄 Сбросить
            </Button>
          </div>

          {/* Info Box */}
          <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
            <h4 className="font-bold mb-2">ℹ️ Как это работает</h4>
            <ul className="text-sm space-y-1 list-disc list-inside text-gray-700">
              <li>Demo виджет показывается на главной странице сайта (Landing Page)</li>
              <li>Посетители общаются с НАСТОЯЩИМ AI ботом</li>
              <li>AI использует информацию из этого поля (точно как у клиентов)</li>
              <li>Виджет работает через backend API с полноценной AI обработкой</li>
              <li>Все изменения применяются сразу после сохранения</li>
              <li>Это ТОЧНО ТОТ ЖЕ функционал, что и у клиентов в личном кабинете</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
